// Copyright 2025, University of Maryland, All Rights Reserved
import React from "react";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { FileText } from "lucide-react";
import { BillActionDropdown } from "@/components/BillActionDropdown";
import { BillScoreCell } from "@/components/workspace-bills/BillScoreCell";
import { AILegislativeReview } from "@/components/workspace-bills/AILegislativeReview.tsx";
import { WorkspaceBill, WorkspaceBillAction } from "@/types/workspace-bill";

interface WorkspaceBillCardProps {
  bill: WorkspaceBill;
  is_selected: boolean;
  session_code: string;
  on_select_bill: (bill_id: string) => void;
  on_process_action: (bill_id: string, action: WorkspaceBillAction) => void;
  on_show_impact_details?: (bill: WorkspaceBill) => void;
  handle_pdf_click: (url: string, e: React.MouseEvent) => void;
  processing_actions?: Record<string, WorkspaceBillAction>;
}

export function WorkspaceBillCard({
  bill,
  is_selected,
  on_select_bill,
  on_process_action,
  on_show_impact_details,
  handle_pdf_click,
  processing_actions,
}: WorkspaceBillCardProps) {
  const formatDate = (dateString?: string) => {
    return dateString ? new Date(dateString).toLocaleString("en-US", {
      timeZone: "America/New_York",
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }) : "";
  };

  return (
    <Card className={`overflow-hidden ${is_selected ? 'ring-2 ring-primary' : ''}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-2">
            <span className="font-medium">{bill.bill_number}</span>
          </div>

          <div className="flex gap-1">
            {bill.pdf_url && bill.full_text_available && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => handle_pdf_click(bill.pdf_url, e)}
                      className="px-2 w-8 h-8"
                    >
                      <FileText className="h-4 w-4" />
                      <span className="sr-only">View Bill PDF</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    View Bill PDF
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>

        <div>
          <p className="text-sm font-medium text-muted-foreground">Title</p>
          <p className="text-sm line-clamp-2">{bill.title || 'No Title'}</p>
        </div>

        <div className="flex justify-between items-center">
          <div>
            <p className="text-sm font-medium text-muted-foreground">Impact</p>
            <BillScoreCell
              analysis={bill.monitor_analysis}
              onClick={() => on_show_impact_details?.(bill)}
            />
          </div>

          {bill.crossfile_bill_number && (
            <div>
              <p className="text-sm font-medium text-muted-foreground">Crossfile</p>
              <Badge variant="outline">{bill.crossfile_bill_number}</Badge>
            </div>
          )}
        </div>

        <div>
          <p className="text-sm font-medium text-muted-foreground">Modified</p>
          <p className="text-sm">{formatDate(bill.last_modified)}</p>
        </div>

        {bill.versions && bill.versions.length > 0 && (
          <div className="flex gap-2">
            <AILegislativeReview
              latestVersion={bill.versions[bill.versions.length - 1]}
              bill={bill}
              loadingAction={processing_actions?.[bill.bill_number]}
            />
          </div>
        )}
      </CardContent>

      <CardFooter className="px-4 py-3 bg-muted/20 flex justify-between">
        <Button
          size="sm"
          variant={is_selected ? "default" : "outline"}
          onClick={() => on_select_bill(bill.bill_number)}
          className="px-3"
        >
          {is_selected ? "Selected" : "Select"}
        </Button>

        <div className="flex gap-2">
          <BillActionDropdown
            onAction={(action: WorkspaceBillAction) => on_process_action(bill.bill_number, action)}
            disabled={!bill.full_text_available || !!processing_actions?.[bill.bill_number]}
          />
        </div>
      </CardFooter>
    </Card>
  );
}